import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PaginationBar from "../components/PaginationBar";
import {
  fetchCategorySuggestions,
  fetchProducts,
  selectCategorySuggestions,
  selectCategorySuggestionsStatus,
  selectProducts,
  selectProductsCount,
  selectProductsError,
  selectProductsStatus,
} from "../features/products/productsSlice";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function asStringList(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((x) => (x == null ? "" : String(x).trim()))
    .filter(Boolean);
}

export default function ProductsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const PAGE_SIZE = 25;

  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [categoryQuery, setCategoryQuery] = useState(() => searchParams.get("category_name") || "");
  const [categoryId, setCategoryId] = useState(() => {
    const v = searchParams.get("category");
    return v != null && String(v).trim() ? v : null;
  });
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(() => {
    const raw = searchParams.get("page");
    const n = Number(raw || 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  });

  const rows = useSelector(selectProducts);
  const totalCount = useSelector(selectProductsCount);
  const loadStatus = useSelector(selectProductsStatus);
  const error = useSelector(selectProductsError);

  const categories = useSelector(selectCategorySuggestions);
  const categoriesStatus = useSelector(selectCategorySuggestionsStatus);

  const searchTrimmed = useMemo(() => search.trim(), [search]);
  const categoryQueryTrimmed = useMemo(() => categoryQuery.trim(), [categoryQuery]);

  // Keep URL in sync, so Back preserves pagination/filters
  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTrimmed) next.set("search", searchTrimmed);
    if (categoryId != null && String(categoryId).trim()) next.set("category", String(categoryId).trim());
    if (categoryQueryTrimmed && categoryId != null) next.set("category_name", categoryQueryTrimmed);
    if (page && page !== 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [searchTrimmed, categoryId, categoryQueryTrimmed, page, setSearchParams]);

  // If user navigates with Back/Forward and URL changes, hydrate local state
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlCategoryName = searchParams.get("category_name") || "";
    const urlCategory = (() => {
      const v = searchParams.get("category");
      return v != null && String(v).trim() ? v : null;
    })();
    const urlPage = (() => {
      const raw = searchParams.get("page");
      const n = Number(raw || 1);
      return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
    })();

    if (urlSearch !== search) setSearch(urlSearch);
    if (urlCategoryName !== categoryQuery) setCategoryQuery(urlCategoryName);
    if (String(urlCategory ?? "") !== String(categoryId ?? "")) setCategoryId(urlCategory);
    if (urlPage !== page) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    dispatch(fetchProducts({ search: searchTrimmed, page, pageSize: PAGE_SIZE, categoryId }));
  }, [dispatch, refreshKey, searchTrimmed, page, categoryId]);

  // Category autocomplete (fetch suggestions)
  useEffect(() => {
    if (!categoryOpen) return;
    dispatch(fetchCategorySuggestions({ search: categoryQueryTrimmed, pageSize: 20 }));
  }, [categoryOpen, categoryQueryTrimmed, dispatch]);

  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));

  useEffect(() => {
    // On hard reload `totalCount` is initially 0 until the first fetch completes.
    // If we clamp here immediately, we will reset `page` to 1 (because totalPages=1),
    // losing the page from URL. Clamp only after we have real data.
    if (loadStatus === "succeeded" && page > totalPages) {
      setPage(totalPages);
    }
  }, [loadStatus, page, totalCount, totalPages]);

  const visibleRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Товары</div>
          <div style={styles.subTitle}>
            Список товаров (данные из <span style={styles.mono}>/api/product/products/</span>).
          </div>
        </div>

        <div style={styles.headerActions}>
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск: name / description / barcode…"
            style={styles.search}
          />
          <div style={styles.categoryWrap}>
            <input
              value={categoryQuery}
              onChange={(e) => {
                setCategoryQuery(e.target.value);
                setCategoryId(null);
                setPage(1);
                setCategoryOpen(true);
              }}
              onFocus={() => setCategoryOpen(true)}
              onBlur={() => {
                // allow clicking an option before closing
                window.setTimeout(() => setCategoryOpen(false), 120);
              }}
              placeholder="Категория (автокомплит)"
              style={styles.search}
            />
            {categoryId != null && (
              <button
                type="button"
                onClick={() => {
                  setCategoryId(null);
                  setCategoryQuery("");
                  setPage(1);
                  setCategoryOpen(false);
                }}
                style={styles.clearBtn}
                title="Сбросить категорию"
              >
                ✕
              </button>
            )}
            {categoryOpen && (
              <div style={styles.suggestBox}>
                {categoriesStatus === "loading" && <Loading inline text="Загрузка…" />}
                {categoriesStatus !== "loading" && categories.length === 0 ? (
                  <div style={styles.suggestHint}>Ничего не найдено</div>
                ) : (
                  categories.slice(0, 20).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      style={{
                        ...styles.suggestItem,
                        ...(String(c.id) === String(categoryId) ? styles.suggestItemActive : null),
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setCategoryId(c.id);
                        setCategoryQuery(c.name || "");
                        setPage(1);
                        setCategoryOpen(false);
                      }}
                      title={`#${c.id}`}
                    >
                      <span style={styles.suggestName}>{c.name || "—"}</span>
                      <span style={styles.suggestMeta}>#{c.id}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <button type="button" onClick={() => setRefreshKey((v) => v + 1)} style={styles.btn}>
            Обновить
          </button>
          <button
            type="button"
            onClick={() =>
              navigate("/warehouse/products/new", {
                state: { from: `${location.pathname}${location.search}` },
              })
            }
            style={styles.btnPrimary}
          >
            + Добавить товар
          </button>
        </div>
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div style={styles.error}>{error}</div>}

      <div style={styles.body}>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Название</th>
                <th style={styles.th}>Barcode</th>
                <th style={styles.th}>Barcodes</th>
                <th style={styles.th}>Описание</th>
                <th style={styles.th}>Цена</th>
                <th style={styles.th}>Стоимость</th>
                <th style={styles.th}>Quick</th>
                <th style={styles.th}>Категории</th>
                <th style={styles.th}>Создано</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && loadStatus === "succeeded" ? (
                <tr>
                  <td colSpan={10} style={styles.tdMuted}>
                    Ничего не найдено
                  </td>
                </tr>
              ) : (
                visibleRows.map((r) => {
                  const barcodes = asStringList(r.barcodes);
                  const categories = Array.isArray(r.categories) ? r.categories : [];
                  const categoryNames = categories.map((c) => c?.name).filter(Boolean);
                  return (
                    <tr
                      key={r.id}
                      style={styles.tr}
                      onClick={() =>
                        navigate(`/warehouse/products/${r.id}/edit`, {
                          state: { from: `${location.pathname}${location.search}` },
                        })
                      }
                      title="Открыть карточку товара"
                    >
                      <td style={styles.tdMono}>{r.id}</td>
                      <td style={styles.td}>{r.name || "—"}</td>
                      <td style={styles.tdMono}>{r.barcode || "—"}</td>
                      <td style={styles.tdMono}>{barcodes.length ? barcodes.join(", ") : "—"}</td>
                      <td style={styles.td}>{r.description || "—"}</td>
                      <td style={styles.tdMono}>{r.cost_price ?? "—"}</td>
                      <td style={styles.tdMono}>{r.self_cost_price ?? "—"}</td>
                      <td style={styles.td}>{r.is_quick ? "Yes" : "No"}</td>
                      <td style={styles.td}>{categoryNames.length ? categoryNames.join(", ") : "—"}</td>
                      <td style={styles.td}>{formatDateTime(r.created_at)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <PaginationBar
            page={page}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            itemsOnPage={visibleRows.length}
            onPageChange={setPage}
            styles={styles}
          />
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { height: "100%", display: "flex", flexDirection: "column", background: "var(--c-bg)", color: "var(--c-text)" },
  mono: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" },
  header: { padding: "16px 24px", borderBottom: "1px solid var(--c-border)", display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 14, flexWrap: "wrap", flexShrink: 0 },
  title: { fontSize: 22, fontWeight: 900, color: "var(--c-text)", margin: 0 },
  subTitle: { marginTop: 3, fontSize: 15, color: "var(--c-text-muted)", lineHeight: 1.35, maxWidth: 760 },
  headerActions: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  categoryWrap: { position: "relative", display: "inline-flex", alignItems: "center" },
  search: { width: 360, maxWidth: "70vw", padding: "8px 12px", borderRadius: 8, border: "1px solid var(--c-border)", background: "var(--c-input)", color: "var(--c-text)", outline: "none", fontSize: 15 },
  clearBtn: { position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", width: 24, height: 24, borderRadius: 99, border: "1px solid var(--c-border)", background: "var(--c-card)", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 700, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 },
  suggestBox: { position: "absolute", left: 0, right: 0, top: "calc(100% + 6px)", zIndex: 50, borderRadius: 16, border: "1px solid var(--c-border)", background: "var(--c-card)", boxShadow: "0 12px 28px rgba(0,0,0,0.5)", overflow: "hidden", maxHeight: 320, overflowY: "auto" },
  suggestItem: { width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", border: "none", borderBottom: "1px solid var(--c-border)", background: "transparent", color: "var(--c-text)", cursor: "pointer", textAlign: "left", fontSize: 15, fontWeight: 600 },
  suggestItemActive: { background: "rgba(245,197,24,0.08)" },
  suggestName: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  suggestMeta: { color: "var(--c-text-muted)", fontWeight: 700, fontSize: 14 },
  suggestHint: { padding: "10px 12px", fontSize: 14, color: "var(--c-text-muted)", fontWeight: 600 },
  btn: { padding: "8px 14px", borderRadius: 99, border: "1px solid var(--c-border-light)", background: "transparent", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 15 },
  btnPrimary: { padding: "8px 14px", borderRadius: 99, border: "none", background: "var(--c-btn)", color: "var(--c-btn-text)", cursor: "pointer", fontWeight: 700, fontSize: 15 },
  notice: { padding: "14px 24px", fontSize: 15, color: "var(--c-text-muted)" },
  error: { padding: "12px 24px", fontSize: 15, background: "rgba(248,113,113,0.1)", borderTop: "1px solid rgba(248,113,113,0.2)", borderBottom: "1px solid rgba(248,113,113,0.2)", color: "var(--c-danger)" },
  body: { padding: "16px 24px", flex: 1, minHeight: 0, overflow: "auto" },
  tableWrap: { border: "1px solid var(--c-border)", background: "var(--c-card)", borderRadius: 16, overflow: "hidden" },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  th: { textAlign: "left", padding: "10px 14px", fontSize: 13, fontWeight: 700, color: "var(--c-text-sub)", background: "var(--c-input)", borderBottom: "1px solid var(--c-border)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.4px" },
  tr: { cursor: "pointer" },
  td: { padding: "10px 14px", fontSize: 15, borderBottom: "1px solid var(--c-border)", verticalAlign: "middle", color: "var(--c-text)" },
  tdMono: { padding: "10px 14px", fontSize: 15, borderBottom: "1px solid var(--c-border)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace", color: "var(--c-text)" },
  tdMuted: { padding: 14, fontSize: 15, color: "var(--c-text-muted)" },
  paginationWrap: { marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
  paginationMeta: { fontSize: 14, color: "var(--c-text-muted)", fontWeight: 600 },
  paginationBtns: { display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  pageBtn: { padding: "7px 10px", borderRadius: 99, border: "1px solid var(--c-border)", background: "var(--c-card)", color: "var(--c-text-sub)", cursor: "pointer", fontWeight: 600, fontSize: 14, minWidth: 34 },
  pageBtnActive: { border: "1px solid rgba(245,197,24,0.4)", background: "var(--c-accent)", color: "var(--c-accent-on)" },
  pageBtnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  pageDots: { color: "var(--c-text-muted)", fontWeight: 700, padding: "0 6px" },
};

